import { useMemo, useEffect } from 'react'
import { sortTodos } from '../../../modules/TodoSort'
import { TodoItem } from '../../../modules/TodoItem'
import { SortType } from '../../../modules/TodoFilter'

interface UseTodoFilteringReturn {
  sortedAndFilteredTodos: TodoItem[]
}

/**
 * 1. filter: searchText 처리
 * 2. sort: sortOption 처리
 * 3. selectedTodo 처리
 *
 * return: sortedAndFilteredTodos: TodoItem[]
 */
export const useTodoFiltering = (
  todoList: TodoItem[] | undefined,
  searchText: string,
  sortOption: SortType,
  sortAscending: boolean,
  selectedTodo: TodoItem | null,
  onTodoSelect: ((todo: TodoItem | null) => void) | null,
  locale: string,
): UseTodoFilteringReturn => {
  // 모든 필터에 대해 DB 우선 처리된 todoList 사용
  const filteredTodos = useMemo(() => {
    // todoList가 undefined나 null인 경우 빈 배열로 처리
    if (!Array.isArray(todoList)) {
      return []
    }
    return todoList.filter(
      (todo) =>
        !searchText ||
        (todo.summary && todo.summary.toLowerCase().includes(searchText.toLowerCase())),
    )
  }, [todoList, searchText])

  // Apply sorting to filtered todos
  const sortedAndFilteredTodos = useMemo(
    () => sortTodos(filteredTodos, sortOption, sortAscending, locale),
    [filteredTodos, sortOption, sortAscending, locale],
  )

  // selectedTodo가 현재 필터링된 결과에 없으면 null로 설정하여 editor 표시 안하도록.
  useEffect(() => {
    if (selectedTodo?.url) {
      const todoExists = sortedAndFilteredTodos.some((todo) => todo.url === selectedTodo.url)

      if (!todoExists && onTodoSelect) {
        onTodoSelect(null)
      }
    }
  }, [sortedAndFilteredTodos, selectedTodo, onTodoSelect])

  return {
    sortedAndFilteredTodos,
  }
}
